import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, Send, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const RADIO_WHATSAPP = "393917041816"; // +39 391 704 1816

const MAX_DURATION_SEC = 90; // max audio length

/**
 * Pick the best MediaRecorder mime type for the current browser.
 * iOS Safari → audio/mp4 (AAC). Chrome/Firefox/Edge/Android → audio/webm (Opus).
 */
function pickRecorderType() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return ""; // let the browser pick a default
}

function extFor(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("wav")) return "wav";
  return "webm";
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ============= WAV ENCODER =============
// WhatsApp/Telegram/Email/etc. all accept .wav as audio attachment.
// WebM/Opus (default Chrome/Android recorder output) is NOT recognised by WhatsApp,
// so we decode the recorded blob, mix down to mono, resample to 22050 Hz and
// re-encode as 16-bit PCM WAV for maximum compatibility.

function writeStr(view, offset, str) {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}

function encodeWAV(samples, sampleRate) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buf);
  // RIFF
  writeStr(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(view, 8, "WAVE");
  // fmt
  writeStr(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);            // PCM
  view.setUint16(22, 1, true);            // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  // data
  writeStr(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return buf;
}

function resampleMono(input, srcRate, dstRate) {
  if (srcRate === dstRate) return input;
  const ratio = srcRate / dstRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const idx = i * ratio;
    const i0  = Math.floor(idx);
    const i1  = Math.min(i0 + 1, input.length - 1);
    const t   = idx - i0;
    out[i] = input[i0] * (1 - t) + input[i1] * t;
  }
  return out;
}

async function convertBlobToWav(blob, targetSampleRate = 22050) {
  const arrayBuffer = await blob.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error("AudioContext non disponibile");

  // decodeAudioData works on any format the browser can natively decode:
  // Chrome/Android: webm/opus, mp4/aac
  // iOS Safari:     mp4/aac (webm/opus from 14.1+)
  // Firefox:        ogg/opus, webm/opus
  const ctx = new Ctx();
  let decoded;
  try {
    // Some browsers want a copy of the buffer (it gets detached on decode)
    decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    try { ctx.close(); } catch (_) {}
  }

  // Mix down to mono
  const n = decoded.length;
  const mono = new Float32Array(n);
  const ch = decoded.numberOfChannels;
  for (let c = 0; c < ch; c++) {
    const data = decoded.getChannelData(c);
    for (let i = 0; i < n; i++) mono[i] += data[i] / ch;
  }
  // Resample if needed
  const resampled = resampleMono(mono, decoded.sampleRate, targetSampleRate);
  const wavBuffer = encodeWAV(resampled, targetSampleRate);
  return new Blob([wavBuffer], { type: "audio/wav" });
}

export default function AudioRecorder({ shareName, shareDecade, onSent }) {
  const [status, setStatus] = useState("idle"); // idle | recording | preview
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 instantaneous mic volume
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [supported, setSupported] = useState(true);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current)   { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!supported) {
      toast.error("Il tuo browser non supporta la registrazione audio");
      return;
    }

    // Pause the live radio stream so it doesn't leak into the microphone recording
    try {
      window.dispatchEvent(new CustomEvent("rrz:radio-pause-request", { detail: { reason: "voice-recording" } }));
    } catch (_) { /* ignore */ }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      // Setup analyser for level meter
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 2.6));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const mimeType = pickRecorderType() || undefined;
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || mimeType || "audio/webm";
        const finalBlob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(finalBlob);
        setBlob(finalBlob);
        setBlobUrl(url);
        setStatus("preview");
        cleanup();
      };
      mediaRecorderRef.current = rec;

      rec.start(250);
      startTsRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const s = (Date.now() - startTsRef.current) / 1000;
        setElapsed(s);
        if (s >= MAX_DURATION_SEC) stopRecording();
      }, 100);
      setStatus("recording");
    } catch (err) {
      console.warn("Mic permission denied or unavailable", err);
      toast.error("Microfono non accessibile", {
        description: "Concedi il permesso al microfono per registrare un audio.",
      });
      cleanup();
    }
  };

  const stopRecording = () => {
    const r = mediaRecorderRef.current;
    if (r && r.state !== "inactive") r.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current)   { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setLevel(0);
  };

  const discard = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setElapsed(0);
    setIsPlaying(false);
    setStatus("idle");
  };

  const togglePreview = () => {
    const a = previewAudioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  useEffect(() => {
    const a = previewAudioRef.current;
    if (!a) return;
    const onPlay  = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, [blobUrl]);

  const buildMessageText = () => {
    const lines = ["Ciao Radio Rumore Zero!"];
    const who = (shareName || "").trim();
    if (who) lines.push(`Sono ${who}`);
    if (shareDecade) lines.push(`Decade del cuore: anni ${shareDecade.replace("s","")}`);
    lines.push("", "Ho registrato un saluto vocale per voi — ve lo invio in allegato.");
    return lines.join("\n");
  };

  const sendShare = async () => {
    if (!blob) return;
    setIsSharing(true);
    try {
      // Convert the recording to WAV — this is the format universally accepted
      // by WhatsApp / Telegram / Email as a real audio attachment. WebM/Opus
      // (default Chrome/Android output) is NOT recognised by WhatsApp.
      let outBlob = blob;
      let outName = `saluto-rumore-zero.${extFor(blob.type)}`;
      try {
        const wavBlob = await convertBlobToWav(blob, 22050);
        outBlob = wavBlob;
        outName = `saluto-rumore-zero.wav`;
      } catch (convErr) {
        console.warn("WAV conversion failed, sending original format", convErr);
        // continue with original blob as best-effort fallback
      }

      const file = new File([outBlob], outName, { type: outBlob.type });
      const text = buildMessageText();
      const title = "Saluto vocale per Radio Rumore Zero";

      // Path A — Web Share API with files (Android Chrome, iOS Safari 15+, modern Edge):
      // System share sheet opens. The user chooses where to send the audio
      // (WhatsApp, Telegram, Messages, Email, ...) AND picks the contact.
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({ title, text, files: [file] });
          toast.success("Condivisione aperta!", {
            description: "Scegli l'app e il contatto a cui inviare il saluto.",
          });
          onSent?.();
          discard();
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return; // user cancelled, keep recording
          console.warn("Web Share failed, falling back to download", err);
        }
      }

      // Path B — Fallback for desktop / browsers without share API:
      // Download the WAV + open WhatsApp chat addressed to the radio so the user
      // attaches the just-downloaded file with one tap.
      const dl = URL.createObjectURL(outBlob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = outName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dl), 5000);

      const waUrl = `https://wa.me/${RADIO_WHATSAPP}?text=${encodeURIComponent(text)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");

      toast.message("Audio scaricato + WhatsApp aperto", {
        description: "Allega il file appena scaricato nella chat con la radio.",
      });
      onSent?.();
      discard();
    } catch (err) {
      toast.error("Errore nell'invio", { description: String(err?.message || err) });
    } finally {
      setIsSharing(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-xl border border-[#332f2b] bg-[#0d0b0a] p-5 text-[#A39A8F] font-mono text-sm">
        Il tuo browser non supporta la registrazione audio. Usa Chrome, Safari o
        Firefox aggiornati.
      </div>
    );
  }

  // ---------------- UI ----------------
  return (
    <div data-testid="audio-recorder" className="rounded-xl border border-[#332f2b] bg-[#0d0b0a] p-5 sm:p-6 space-y-5">
      {/* Display: timer + waveform */}
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00]">
          {status === "recording" ? "Registrazione in corso" : status === "preview" ? "Anteprima" : "Pronto a registrare"}
        </div>
        <div className="font-anton text-2xl tabular-nums text-[#F5F2EB]" data-testid="recorder-timer">
          {formatTime(elapsed)} <span className="text-[#69635c] text-base">/ {formatTime(MAX_DURATION_SEC)}</span>
        </div>
      </div>

      {/* Level meter (during recording) or progress bar (preview) */}
      {status === "recording" ? (
        <div data-testid="recorder-meter" className="flex items-center gap-[3px] h-12 px-1">
          {Array.from({ length: 28 }).map((_, i) => {
            const threshold = (i + 1) / 28;
            const active = level >= threshold * 0.85;
            const h = active ? 100 : 18;
            return (
              <span
                key={i}
                className="flex-1 rounded-sm transition-[height,background-color] duration-75"
                style={{
                  height: `${h}%`,
                  backgroundColor: active ? "#FF6B00" : "#2b2520",
                  boxShadow: active ? "0 0 8px rgba(255,107,0,0.6)" : "none",
                }}
              />
            );
          })}
        </div>
      ) : status === "preview" && blobUrl ? (
        <div className="flex items-center gap-3 px-1">
          <button
            type="button"
            data-testid="recorder-preview-toggle"
            onClick={togglePreview}
            className="w-11 h-11 rounded-full bg-[#FF6B00] text-[#12100e] flex items-center justify-center hover:bg-[#ff8226] active:scale-95 transition"
            aria-label={isPlaying ? "Pausa" : "Ascolta"}
          >
            {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
          </button>
          <audio ref={previewAudioRef} src={blobUrl} preload="metadata" className="flex-1 min-w-0 w-full" controls />
        </div>
      ) : (
        <div className="h-12 rounded-md border border-dashed border-[#332f2b] flex items-center justify-center font-mono text-xs text-[#69635c]">
          Tocca il pulsante rosso per iniziare a registrare
        </div>
      )}

      {/* Actions */}
      {status === "idle" && (
        <button
          type="button"
          data-testid="recorder-start"
          onClick={startRecording}
          className="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-lg bg-[#d92020] text-white font-oswald uppercase tracking-wider hover:bg-[#ef2828] active:scale-[0.99] transition shadow-[0_0_20px_rgba(217,32,32,0.45)]"
        >
          <Mic size={18} />
          Registra messaggio vocale
        </button>
      )}

      {status === "recording" && (
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="recorder-stop"
            onClick={stopRecording}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#FF6B00] text-[#12100e] font-oswald uppercase tracking-wider hover:bg-[#ff8226] active:scale-[0.99] transition"
          >
            <Square size={16} fill="currentColor" />
            Ferma registrazione
          </button>
        </div>
      )}

      {status === "preview" && (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="recorder-discard"
            onClick={discard}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-[#332f2b] text-[#A39A8F] hover:border-[#d92020] hover:text-[#d92020] active:scale-[0.99] transition font-oswald uppercase tracking-wider"
          >
            <Trash2 size={16} />
            Scarta
          </button>
          <button
            type="button"
            data-testid="recorder-send"
            onClick={sendShare}
            disabled={isSharing}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#FF6B00] text-[#12100e] font-oswald uppercase tracking-wider hover:bg-[#ff8226] active:scale-[0.99] transition disabled:opacity-50 shadow-[0_0_20px_rgba(255,107,0,0.35)]"
          >
            {isSharing
              ? <><Loader2 size={16} className="animate-spin" /> Invio…</>
              : (typeof navigator !== "undefined" && navigator.canShare)
                ? <><Send size={16} /> Condividi audio</>
                : <><Download size={16} /> Scarica & WhatsApp</>}
          </button>
        </div>
      )}

      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#69635c] text-center">
        Durata massima: {MAX_DURATION_SEC} secondi · Scegli tu come e a chi inviare l'audio
      </div>
    </div>
  );
}
